import React, { useState, useEffect } from 'react';

import {
  Card,
  Input,
  Select,
  Text,
  Radio,
  TextArea,
  Tooltip
} from 'v3/components';

import { QuestionIcon } from 'v3/pages/Load/Register/LoadData/styled';

import CheckBox from 'components/CheckBox';

import { usePreLoad } from '../context';
import { Col, Row } from 'react-bootstrap';

import {
  fetchCostCenter,
  fetchProductAndCategorie,
  fetchSelectOptions,
  fetchRiskManagers
} from 'utils/fetches';

import api from 'services/api';
import CurrencyInput from 'v3/components/Input/Currency';
import { validateDateTime } from 'v3/utils/functions';
import { checkIfBlacklisted } from 'functions/index'

export function LoadData() {
  const {
    data,
    setData,
    snackbar,
    errors,
    setErrors,
    loading,
    loadingDuplicate
  } = usePreLoad();

  const [validatingTaker, setValidatingTaker] = useState(false);

  const loadSymbol = data?.currency?.value === 'BRL' ? 'R$' : '$';
  async function validateClientBlacklist(cpfCnpj) {
    try {
      const response = await api.get(`v3/blacklist-person/?value=${cpfCnpj}`);
      if (response.data.total > 0) {
        snackbar.show(
          <>
            <Text>
              Não é possivel selecionar o Tomador{' '}
              {response.data.data[0].description} - {cpfCnpj} pois ele está
              banido!{' '}
            </Text>
            <Text>Motivo: {response.data.data[0].reason}</Text>
          </>,
          {
            type: 'error',
          }
        );
        setData(old => ({ ...old, client: null }));
      }
      return true;
    } catch (error) {
      snackbar.show(
        <Text>
          Error ao validar o Tomador de CNPJ: {cpfCnpj} na lista de banidos!
        </Text>,
        {
          type: 'error',
        }
      );
      setData(old => ({ ...old, client: null }));
      return false;
    }
  }

  async function handleTakerChange(value) {
    const onBlacklistedCallback = blacklist =>
      snackbar.show(
        <Col style={{ display: 'flex', flexDirection: 'column', gap: '0.5' }}>
          <Text>
            Não foi possível selecionar o cliente pois ele está bloqueado no blacklist
          </Text>
          {
            blacklist?.logs.length > 0 && (
              <Text>
                Descrição: {blacklist?.logs[0]?.description}
              </Text>
            )
          }
        </Col>,
        {
          type: 'error',
        }
      );
    const isBlackListed = await checkIfBlacklisted(
      value?.id,
      onBlacklistedCallback,
    );
    if (isBlackListed) return;

    let isClientValid = false;
    setValidatingTaker(true);
    if (value) {
      isClientValid = await validateClientBlacklist(value?.cgccpf);
      if (isClientValid) {
        setData(old => ({ ...old, taker: value }));
      }
    } else {
      setData(old => ({ ...old, taker: null }));
    }
    setValidatingTaker(false);
  }

  useEffect(() => {
    if (Number(data?.loadCreationNumber) > 1) {
      setData(old => ({
        ...old,
        clientOrderNumber: '',
      }));
    }
  }, [data?.loadCreationNumber]);

  return (
    <Card
      header={
        <Text color="#464E5F" type="header">
          Dados da Carga
        </Text>
      }
      bodyCardPadding="1rem 3rem"
      HeaderRightComponent={
        <div className="position-relative w-25">
          <Input
            type="number"
            onKeyDown={evt =>
              ['e', 'E', '+', '-', '.'].includes(evt.key) &&
              evt.preventDefault()
            }
            placeholder="Quantidade"
            hideAppearance
            value={data?.loadCreationNumber}
            onChange={event => {
              if (Number(event.target.value) > 100) {
                return;
              } else
                setData(old => ({
                  ...old,
                  loadCreationNumber: event.target.value,
                }));
            }}
            error={errors?.loadCreationNumber}
            containerStyle={{ marginBottom: 0 }}
            disabled={loading}
          />
          <Tooltip
            content={
              <Text type="label">
                Quantidade de pré cargas a serem criadas *
              </Text>
            }
          >
            <QuestionIcon />
          </Tooltip>
        </div>
      }
    >
      <Row className="form">
        <Col xs={12} md={6} className="mb-3">
          <Select.Async
            label="Cliente *"
            onSearch={search =>
              fetchSelectOptions('persons/customers', { search })
            }
            value={data?.taker}
            getOptionLabel={option => {
              let label = `${option?.social_name} - ${option?.cgccpf}`;

              if (option.city) {
                label += ` - ${option?.city}`;
              }

              return label;
            }}
            getOptionValue={option => option.id}
            error={errors.taker}
            onChange={handleTakerChange}
            isDisabled={loading || loadingDuplicate}
            loading={validatingTaker}
          />
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <Select.Async
            label="CC (Centro de custo) *"
            onSearch={search => fetchCostCenter({ search })}
            value={data?.costCenter}
            onChange={value => {
              setData(old => ({ ...old, costCenter: value }));
            }}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            isDisabled={loading || loadingDuplicate}
            error={errors.costCenter}
          />
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <Select.Async
            label="Produto"
            onSearch={search => fetchProductAndCategorie(search)}
            value={data?.product}
            getOptionLabel={option =>
              `${`${option.name} - ${option.product_type_name}`}`
            }
            getOptionValue={option =>
              `${`${option?.id}-${option?.product_type_id}`}`
            }
            isDisabled={loading || loadingDuplicate}
            onChange={value => setData(old => ({ ...old, product: value }))}
          />
        </Col>
        <Col md={6} xs={12} className="mb-3">
          <Input
            label="Número do pedido do cliente"
            value={data?.clientOrderNumber}
            onChange={event =>
              setData(old => ({
                ...old,
                clientOrderNumber: event.target.value,
              }))
            }
            disabled={loading || Number(data?.loadCreationNumber) > 1 || loadingDuplicate}
          />
        </Col>
        <Col md={4} xs={12}>
          <CurrencyInput
            label={`Frete (${loadSymbol || 'R$'})`}
            value={data?.takerValue}
            onChange={(_event, value) => {
              if (_event?.target?.value?.length > 9) {
                return;
              }
              setData(old => ({ ...old, takerValue: value }));
            }}
            disabled={loading || loadingDuplicate}
          />
        </Col>
        <Col xs={4} className="mb-3">
          <Input
            label="Peso (kg)"
            type="number"
            min={1}
            value={data?.weight}
            onChange={({ target }) => {
              if (target?.value?.length > 10) {
                return;
              }
              setData(old => ({ ...old, weight: target.value }));
            }}
            disabled={loading || loadingDuplicate}
          />
        </Col>
        <Col xs={4} className="mb-3">
          <Input
            label="Quantidade de Pallets"
            type="number"
            min={1}
            value={data?.pallets}
            onChange={({ target }) => {
              setData(old => ({ ...old, pallets: target.value }));
            }}
            disabled={loading || loadingDuplicate}
          />
        </Col>
        <Col md={4} xs={12} className="mb-3">
          <Text as="span" color="dark" type="medium" weight={500}>
            Buscar motoristas no APP
          </Text>
          <CheckBox
            disabled={loading || loadingDuplicate}
            label="Sim"
            labelStyle={{
              textTransform: 'capitalize',
              color: '#464E5F',
            }}
            value={data?.searchDriver}
            checked={data?.searchDriver}
            onChange={(e, checked) => {
              if (checked === false) {
                setData(old => ({
                  ...old,
                  searchDriverRadius: '0',
                }));
                setErrors(old => ({ ...old, searchDriverRadius: null }));
              } else {
                setData(old => ({
                  ...old,
                  searchDriverRadius: 500,
                }));
              }
              setData(old => ({
                ...old,
                searchDriver: checked,
              }));
            }}
          />
        </Col>

        <Col md={4} xs={12} className="mb-3">
          <Input
            label={
              data?.searchDriver
                ? 'Raio de busca de motorista (Km)*'
                : 'Raio de busca de motorista (Km)'
            }
            tooltip="Máximo de 500km"
            value={data?.searchDriverRadius}
            onKeyDown={evt =>
              ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
            }
            onChange={event => {
              if (
                Number(event?.target?.value) > 500 ||
                Number(event?.target?.value) < 0 ||
                event?.target?.value.length > 6
              ) {
                return;
              }
              setData(old => ({
                ...old,
                searchDriverRadius: event.target.value,
              }));
            }}
            error={errors.searchDriverRadius}
            type="number"
            min="1"
            max="500"
            disabled={
              loading ||
              data?.searchDriver === false ||
              data?.searchDriver === null ||
              loadingDuplicate
            }
          />
        </Col>
        <Col md={4} xs={12} className="mb-3">
          <Input
            type="datetime-local"
            value={data?.dueDate}
            label="Data de vencimento *"
            onChange={event => {
              let isValid = validateDateTime(event?.target?.value)
              if (isValid) {
                setData(old => ({ ...old, dueDate: event?.target?.value }));
                setErrors(old => ({ ...old, dueDate: null }));
              } else {
                setErrors(old => ({ ...old, dueDate: 'Data inválida' }));
              }
            }}
            disabled={loading || loadingDuplicate}
            error={errors.dueDate}
          />
        </Col>
        <Col md={4} xs={12} className="mb-3">
          <Radio.Group
            label="Rastreado *"
            onChange={({ target }) =>
              setData(old => ({
                ...old,
                isTracked: target.value,
                riskManager: null
              }))
            }
            value={data?.isTracked}
            horizontal
            error={errors?.isTracked}
            disabled={loading || loadingDuplicate}
          >
            <Radio id="radio-novo-veiculo-modal-rastreado-sim" value>
              <Text color="dark" type="label">
                Sim
              </Text>
            </Radio>
            <Radio
              id="radio-novo-veiculo-modal-rastreado-nao"
              value={false}
            >
              <Text color="dark" type="label">
                Não
              </Text>
            </Radio>
          </Radio.Group>
        </Col>
        <Col md={6} xs={12} className="mb-3">
          {
            data.isTracked && (
              <Select.Async
                label="Gerenciadora"
                onSearch={search => fetchRiskManagers(search)}
                value={data?.riskManager}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                onChange={value => {
                  setData(old => ({ ...old, riskManager: value }));
                }}
                isDisabled={loading || loadingDuplicate}
                error={errors?.riskManager}
              />
            )
          }
        </Col>
        <Col md={8} xs={12} className="mb-3">
          <Radio.Group
            label="Publicar carga?"
            onChange={({ target }) => {
              setData(old => ({
                ...old,
                loadPublish: target.value,
              }))
            }}
            value={data?.loadPublish}
            horizontal
            disabled={loading || loadingDuplicate}
          >
            <Radio value={true} id="radio-publicar-carga-sim">
              <Text color="dark" type="label">
                Sim
              </Text>
            </Radio>
            <Radio value={false} id="radio-publicar-carga-nao">
              <Text color="dark" type="label">
                Não
              </Text>
            </Radio>
          </Radio.Group>
        </Col>
        <Col xs={12} className="mb-3">
          <TextArea
            label="Observações"
            resizable={false}
            value={data?.observations}
            onChange={event =>
              setData(old => ({
                ...old,
                observations: event.target.value,
              }))
            }
            disabled={loading || loadingDuplicate}
          />
        </Col>
      </Row>
    </Card>
  );
}